import type { ComponentProps } from "react";
import { useSelector } from "react-redux";
import { BrandMark } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
  getUserType,
  isSuperAdmin,
  showCTAButton
} from "../../helpers/helpers";
import { individualSelectors } from "../../redux/individual/selectors";

type CustomButtonProps = {
  title: string;
  type: "ai" | "standard";
  isLoading?: boolean;
  isDisabled?: boolean;
  isPremium?: boolean;
  onClick: () => void;
} & Omit<ComponentProps<typeof Button>, "type" | "title" | "onClick">;

export const CustomButton = ({
  title,
  type,
  isLoading = false,
  isDisabled = false,
  isPremium: _isPremium = false,
  className,
  ...rest
}: CustomButtonProps) => {
  const loginType = getUserType();

  const indivId = useSelector(individualSelectors.selectUid);
  const shouldShowCTAButton = showCTAButton(indivId);

  // Gate only AI buttons for individuals or when CTA should be hidden.
  if (type === "ai") {
    if (loginType === "individual" && !isSuperAdmin) return null;
    if (!shouldShowCTAButton) return null;
    return (
      <Button
        variant="primary"
        disabled={isDisabled || isLoading}
        className={cn("group my-4 w-fit place-self-center", className)}
        {...rest}
      >
        {isLoading ? (
          <Spinner />
        ) : (
          <BrandMark
            mono
            title="Extract information"
            className="size-4 group-hover:animate-pulse"
          />
        )}
        {title}
      </Button>
    );
  }

  return (
    <Button disabled={isDisabled || isLoading} className={className} {...rest}>
      {isLoading ? <Spinner /> : null}
      {title}
    </Button>
  );
};
